import { useState } from 'react';
import { useStudentsList, useAddStudentsToSection } from '../../../api/hooks';
import { personName } from '../../../lib/names';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Checkbox from '../../../components/ui/Checkbox';
import PagedFooter from '../../../components/ui/PagedFooter';
import usePagination from '../../../lib/usePagination';

interface Props {
  gradeId: number;
  sectionId: number;
  onClose: () => void;
}

export default function AddStudentsModal({ gradeId, sectionId, onClose }: Props) {
  const q = useStudentsList({ grade: gradeId, section: 'none' });
  const results: any[] = q.data?.results ?? [];
  const pg = usePagination(results, 10);
  const addMut = useAddStudentsToSection(sectionId);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [error, setError] = useState<string | null>(null);

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function submit() {
    setError(null);
    try {
      const result = await addMut.mutateAsync({ student_ids: Array.from(selected) });
      if (result.errors.length) {
        setError(`${result.moved} moved · ${result.errors.length} errors`);
      } else {
        onClose();
      }
    } catch {
      setError('Add failed.');
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Add students to section"
      size="md"
      footer={(
        <>
          <Button variant="ghost" onClick={onClose} type="button">Cancel</Button>
          <Button
            variant="secondary"
            type="button"
            onClick={submit}
            disabled={selected.size === 0 || addMut.isPending}>
            {`Add ${selected.size} student${selected.size === 1 ? '' : 's'}`}
          </Button>
        </>
      )}>
      {q.isLoading && <p className="text-xs text-muted">Loading…</p>}
      <ul>
        {pg.pageItems.map((r: any) => (
          <li key={r.id} className="border-b border-line last:border-b-0">
            <label className="flex min-h-[44px] cursor-pointer items-center gap-3 text-sm text-ink">
              <Checkbox
                aria-label={`Select ${r.first_name} ${r.last_name}`}
                checked={selected.has(r.id)}
                onChange={() => toggle(r.id)}
              />
              <span>{personName(r)}</span>
            </label>
          </li>
        ))}
      </ul>
      <PagedFooter from={pg.from} to={pg.to} total={pg.total} page={pg.page} pageCount={pg.pageCount} onPage={pg.setPage} className="mt-3" />
      {error && <p role="alert" className="mt-2 text-xs text-danger">{error}</p>}
    </Modal>
  );
}
